import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import ReactApexChart from 'react-apexcharts'

import { GET, httpOk } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'

const QUICK_FILTERS = [
	{ id: 'today', key: 'filter_today' },
	{ id: 'yesterday', key: 'filter_yesterday' },
	{ id: 'week', key: 'filter_7days' },
	{ id: 'month', key: 'filter_30days' },
]

function getDateRange(id) {
	const now = new Date()
	const fmt = d => formatDate(d, 'yyyy-MM-dd')
	if (id === 'today') return { start: fmt(now), end: fmt(now) }
	if (id === 'yesterday') {
		const d = new Date(now); d.setDate(d.getDate() - 1)
		return { start: fmt(d), end: fmt(d) }
	}
	if (id === 'week') {
		const d = new Date(now); d.setDate(d.getDate() - 6)
		return { start: fmt(d), end: fmt(now) }
	}
	const d = new Date(now); d.setDate(d.getDate() - 29)
	return { start: fmt(d), end: fmt(now) }
}

// so'm -> "X.X mln" / "X.X mlrd"
const mln = (v) => {
	v = Number(v) || 0
	const a = Math.abs(v)
	if (a >= 1e9) return (v / 1e9).toFixed(1) + ' mlrd'
	if (a >= 1e6) return (v / 1e6).toFixed(1) + ' mln'
	if (a >= 1e3) return Math.round(v / 1e3) + ' ming'
	return String(Math.round(v))
}
const pct = (v) => (v > 0 ? '+' : '') + (Number(v) || 0) + '%'
const capPct = (v) => (Math.abs(v) > 999 ? (v > 0 ? '+999%+' : '−999%') : pct(v))

const TYPE_COLORS = { Profnastil: '#4f79e8', Tunukafon: '#f2996a', Aksessuar: '#22bfa9' }
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0]
const DOW_LABELS = { 1: 'Du', 2: 'Se', 3: 'Cho', 4: 'Pa', 5: 'Ju', 6: 'Sha', 0: 'Ya' }

function Delta({ v }) {
	if (v === 0 || v === undefined) return <span className="u-delta u-flat">0%</span>
	return <span className={'u-delta ' + (v > 0 ? 'u-up' : 'u-down')}>{v > 0 ? '▲' : '▼'} {capPct(v).replace(/^[+−-]/, '')}</span>
}

function Unit() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [quick, setQuick] = useState('month')
	const [range, setRange] = useState(getDateRange('month'))
	const [custom, setCustom] = useState({ start: null, end: null })
	const [dept, setDept] = useState('all')

	const load = useCallback(async (r) => {
		const res = await GET('/analytics/unit', { start_date: r.start, end_date: r.end }, { loader: true })
		if (httpOk(res)) setData(res.data)
	}, [])

	useEffect(() => { load(range) }, [range, load])

	function pickQuick(id) {
		setQuick(id); setCustom({ start: null, end: null }); setRange(getDateRange(id))
	}
	function applyCustom(start, end) {
		setCustom({ start, end })
		if (start && end) { setQuick(''); setRange({ start: formatDate(start, 'yyyy-MM-dd'), end: formatDate(end, 'yyyy-MM-dd') }) }
	}

	const d = data
	const bucket = d ? (d.moves.by_type[dept] || d.moves.by_type['all']) : null
	const hoursMax = d ? Math.max(...d.hours, 1) : 1
	const peakHour = d ? d.hours.indexOf(Math.max(...d.hours)) : 0
	const dowMax = d ? Math.max(...d.weekdays, 1) : 1
	let bestDow = 0, worstDow = 0
	if (d) {
		let bv = -1, wv = Infinity
		d.weekdays.forEach((v, i) => { if (v > bv) { bv = v; bestDow = i } if (v < wv) { wv = v; worstDow = i } })
	}

	const chartOpts = {
		chart: { type: 'area', height: 150, sparkline: { enabled: false }, toolbar: { show: false }, zoom: { enabled: false } },
		stroke: { curve: 'smooth', width: 2.5, colors: ['#4f79e8'] },
		fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.03 }, colors: ['#4f79e8'] },
		dataLabels: { enabled: false },
		xaxis: { categories: d ? d.chart90.dates : [], labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: '10px' } }, tickAmount: 8, axisTicks: { show: false } },
		yaxis: { labels: { formatter: (v) => mln(v), style: { fontSize: '10px' } } },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		tooltip: { y: { formatter: (v) => formatMoney(v) + ' so\'m' } },
		colors: ['#4f79e8'],
	}

	return (
		<div className="unit-dash">
			<style>{UNIT_CSS}</style>

			<div className="u-top">
				<div className="u-brand">
					<div className="u-mark">
						<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 17l4-4 4 4 4-4 4 4 4-4" /><path d="M2 12l4-4 4 4 4-4 4 4 4-4" /></svg>
					</div>
					<div>
						<h4 className="u-h1">{t('unit_dashboard')}</h4>
						<div className="u-sub">{t('one_branch')} · {t('sum')} · {d ? `${formatDate(d.period.start)} — ${formatDate(d.period.end)}` : '...'}</div>
					</div>
				</div>
				<div className="u-chips">
					{QUICK_FILTERS.map(f => (
						<button key={f.id} className={'u-chip' + (quick === f.id ? ' on' : '')} onClick={() => pickQuick(f.id)}>{t(f.key)}</button>
					))}
					<span className="u-dp">
						<DatePicker selectsRange startDate={custom.start} endDate={custom.end} dateFormat="dd.MM.yyyy"
							onChange={([s, e]) => applyCustom(s, e)} placeholderText={t('select_date')} className="u-dp-input" isClearable />
					</span>
				</div>
			</div>

			{!d ? <div className="u-empty">{t('loading')}</div> : (
				<div className="u-grid">

					{/* KPI */}
					<div className="u-card u-kpi hero c3">
						<div className="u-lbl">{t('kpi_sales')}</div>
						<div className="u-big">{mln(d.kpi.sales)}</div>
						<Delta v={d.kpi.sales_delta} />
						<div className="u-foot">{formatMoney(d.kpi.sales)} so'm</div>
					</div>
					<div className="u-card u-kpi c3">
						<div className="u-lbl">{t('kpi_cheques')}</div>
						<div className="u-big">{d.kpi.cheques}</div>
						<Delta v={d.kpi.cheques_delta} />
						<div className="u-foot">o'rt. chek {mln(d.kpi.avg_cheque)}</div>
					</div>
					<div className="u-card u-kpi c3">
						<div className="u-lbl">{t('kpi_tonna')}</div>
						<div className="u-big">{d.kpi.tonna} <span className="u-unit">t</span></div>
						<Delta v={d.kpi.tonna_delta} />
						<div className="u-foot">{formatMoney(d.kpi.metr)} m</div>
					</div>
					<div className="u-card u-kpi c3">
						<div className="u-lbl">{t('kpi_profit')}</div>
						<div className={'u-big ' + (d.kpi.profit < 0 ? 'u-neg' : '')}>{mln(d.kpi.profit)}</div>
						<Delta v={d.kpi.profit_delta} />
						<div className="u-foot">marja <b className={d.kpi.margin < 0 ? 'u-neg' : ''}>{d.kpi.margin}%</b></div>
					</div>

					{/* HEALTH */}
					<div className="u-card c5">
						<div className="u-hd"><span className="u-ic">◈</span>{t('company_health')}<span className="u-r">100 balldan</span></div>
						<div className="u-health">
							<div className="u-ring" style={{ background: `conic-gradient(#4f79e8 ${d.health.total}%, #eef1f6 0)` }}>
								<div className="u-ring-in"><b>{d.health.total}</b><span>{d.health.total >= 70 ? 'Yaxshi' : d.health.total >= 45 ? "O'rta" : 'Past'}</span></div>
							</div>
							<div className="u-hrows">
								{d.health.items.map((h, i) => {
									const cls = h.score >= 15 ? 'good' : h.score >= 9 ? 'warn' : 'bad'
									return (
										<div className={'u-hrow ' + cls} key={i}>
											<span className="u-hn">{h.name}</span>
											<span className="u-bar"><i style={{ width: (h.score / 20 * 100) + '%' }} /></span>
											<span className="u-hsc">{h.score}/20</span>
										</div>
									)
								})}
							</div>
						</div>
					</div>

					{/* PIPELINE */}
					<div className="u-card c4">
						<div className="u-hd"><span className="u-ic">⚙</span>{t('production_pipeline')}</div>
						<div className="u-funnel">
							<div className="u-fn"><span className="u-fl">{t('st_in_production')}</span><div className="u-fbar" style={{ background: '#f2996a', width: '100%' }}>{d.pipeline.in_production}</div></div>
							<div className="u-fn"><span className="u-fl">{t('st_ready')}</span><div className="u-fbar" style={{ background: '#22bfa9', width: '70%' }}>{d.pipeline.ready}</div></div>
							<div className="u-fn"><span className="u-fl">{t('st_shipped')}</span><div className="u-fbar" style={{ background: '#4f79e8', width: '92%' }}>{d.pipeline.shipped}</div></div>
						</div>
					</div>

					{/* PLAN (soon) */}
					<div className="u-card c3 u-soon">
						<div className="u-hd"><span className="u-ic">◎</span>{t('plan_completion')}</div>
						<div className="u-ghost">
							<div className="u-big" style={{ fontSize: 24 }}>—%</div>
							<div className="u-bar" style={{ height: 9, margin: '12px 0' }}><i style={{ width: 0 }} /></div>
							<div className="u-foot">prognoz: —</div>
						</div>
						<div className="u-veil"><span className="u-badge">{t('coming_soon')}</span></div>
					</div>

					{/* PRODUCT TYPE */}
					<div className="u-card c12">
						<div className="u-hd"><span className="u-ic">▦</span>{t('sales_by_type')}<span className="u-r">so'm / tonna</span></div>
						<div className="u-ptype">
							{d.by_type.map((pt, i) => (
								<div className="u-pt" key={i}>
									<span className="u-pn"><span className="u-sw" style={{ background: TYPE_COLORS[pt.name] }} />{pt.name}</span>
									<span className="u-pv">{mln(pt.sales)}</span>
									<div className="u-track"><i style={{ width: pt.share + '%', background: TYPE_COLORS[pt.name] }} /></div>
									<div className="u-meta">
										<span>{t('share_word')} <b>{pt.share}%</b></span>
										<span>{t('tonna_word')} <b>{pt.tonna} t</b></span>
										<span>{t('margin_word')} <b className={pt.margin < 0 ? 'u-neg' : ''}>{pt.margin}%</b></span>
										<span>{t('kpi_cheques')} <b>{pt.cheques}</b></span>
									</div>
								</div>
							))}
						</div>
					</div>

					{/* GROWING / FALLING — BO'LIM bo'yicha */}
					<div className="u-card c12">
						<div className="u-hd"><span className="u-ic">⇅</span>{t('growing_falling')}<span className="u-r">bo'lim bo'yicha</span></div>
						<div className="u-tabs">
							<button className={'u-tab' + (dept === 'all' ? ' on' : '')} onClick={() => setDept('all')}>{t('all')}</button>
							{d.moves.types.map(mt => (
								<button key={mt.id} className={'u-tab' + (dept === mt.id ? ' on' : '')} onClick={() => setDept(mt.id)}>{mt.name}</button>
							))}
						</div>
						<div className="u-move2">
							<div>
								<div className="u-move-h up"><span>▲</span> {t('growing')}</div>
								<MoveList items={bucket.growing} color="var(--up)" />
							</div>
							<div>
								<div className="u-move-h dn"><span>▼</span> {t('falling')}</div>
								<MoveList items={bucket.falling} color="var(--dn)" />
							</div>
						</div>
					</div>

					{/* PEAK HOURS */}
					<div className="u-card c7">
						<div className="u-hd"><span className="u-ic">◔</span>{t('peak_hours')}<span className="u-r">savdo qachon ko'p</span></div>
						<div className="u-heat">
							{d.hours.map((v, i) => {
								const a = v / hoursMax
								return <i key={i} title={`${i}:00 — ${formatMoney(v)}`} style={{ background: v > 0 ? `rgba(79,121,232,${0.1 + a * 0.8})` : '#eef1f6' }} />
							})}
							<div className="u-heat-ax"><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></div>
						</div>
						<div className="u-note">Eng gavjum: <b>{peakHour}:00</b></div>
					</div>

					{/* WEEKDAY */}
					<div className="u-card c5">
						<div className="u-hd"><span className="u-ic">▥</span>{t('best_worst_day')}</div>
						<div className="u-week">
							{DOW_ORDER.map(idx => {
								const v = d.weekdays[idx]
								const cls = idx === bestDow ? ' best' : idx === worstDow ? ' worst' : ''
								return (
									<div className={'u-wd' + cls} key={idx}>
										<div className="u-wb" style={{ height: Math.max(4, v / dowMax * 100) + '%' }} title={formatMoney(v)} />
										<div className="u-wl">{DOW_LABELS[idx]}</div>
									</div>
								)
							})}
						</div>
						<div className="u-note">Eng yaxshi: <b style={{ color: '#1f8f5e' }}>{DOW_LABELS[bestDow]}</b> · eng past: <b style={{ color: '#cc4256' }}>{DOW_LABELS[worstDow]}</b></div>
					</div>

					{/* 90d CHART */}
					<div className="u-card c8">
						<div className="u-hd"><span className="u-ic">◤</span>{t('sales_dynamics')}<span className="u-r">oxirgi 90 kun</span></div>
						<ReactApexChart options={chartOpts} series={[{ name: 'Savdo', data: d.chart90.sales }]} type="area" height={160} />
					</div>

					{/* WEEKLY TREND */}
					<div className="u-card c4">
						<div className="u-hd"><span className="u-ic">▸</span>{t('weekly_trend')}</div>
						<div className="u-wtrend">
							{d.weekly.length === 0 && <div className="u-foot">{t('no_data')}</div>}
							{d.weekly.map((w, i) => {
								const prev = i > 0 ? d.weekly[i - 1].sales : 0
								const ch = prev > 0 ? Math.round((w.sales - prev) / prev * 100) : 0
								const cur = i === d.weekly.length - 1
								return (
									<div className={'u-wt' + (cur ? ' cur' : '')} key={i}>
										<div className="u-wtl">{w.week}{cur ? ' · joriy' : ''}</div>
										<div className="u-wtv">{mln(w.sales)}</div>
										{i > 0 && <Delta v={ch} />}
									</div>
								)
							})}
						</div>
					</div>

					{/* LOW STOCK */}
					<div className="u-card c4">
						<div className="u-hd"><span className="u-ic">▧</span>{t('stock_low')}</div>
						<div className="u-list2">
							{d.low_stock.length === 0 && <div className="u-foot">{t('no_data')}</div>}
							{d.low_stock.map((s, i) => (
								<div className="u-l2" key={i}>
									<span className={'u-dot' + (i < 2 ? ' hi' : '')} />
									<span className="u-l2n">{s.name}</span>
									<span className="u-l2v">{formatMoney(Math.round(s.remaining))}</span>
								</div>
							))}
						</div>
					</div>

					{/* RECEIVABLES */}
					<div className="u-card c4">
						<div className="u-hd"><span className="u-ic">▪</span>{t('receivables')}<span className="u-r">mijozlar</span></div>
						<div className="u-sumline"><span className="u-sbig">{mln(d.debt.total)}</span><span className="u-sl">jami qarz</span></div>
						<div className="u-list2">
							{d.debt.top.map((dc, i) => (
								<div className="u-l2" key={i}>
									<span className={'u-dot' + (i === 0 ? ' hi' : '')} />
									<span className="u-l2n">{dc.name || '—'}</span>
									<span className="u-l2v">{mln(dc.amount)}</span>
								</div>
							))}
						</div>
					</div>

					{/* DELIVERY */}
					<div className="u-card c4">
						<div className="u-hd"><span className="u-ic">▭</span>{t('delivery')}</div>
						<div className="u-deliv">
							<div className="u-dv"><div className="u-dn">{d.delivery.share}%</div><div className="u-dl">ulush</div></div>
							<div className="u-dv"><div className="u-dn">{d.delivery.count}</div><div className="u-dl">buyurtma</div></div>
							<div className="u-dv"><div className="u-dn">{mln(d.delivery.avg)}</div><div className="u-dl">o'rt. summa</div></div>
						</div>
						<div className="u-note" style={{ fontSize: 11 }}>Yetkazish vaqti hozircha kuzatilmaydi.</div>
					</div>

				</div>
			)}
		</div>
	)
}

function MoveList({ items, color, sign }) {
	const { t } = useTranslation()
	if (!items || items.length === 0) return <div className="u-foot">{t('no_data')}</div>
	const max = Math.max(...items.map(i => Math.abs(i.change)), 1)
	return (
		<div className="u-plist">
			{items.map((m, i) => (
				<div className="u-pl" key={i}>
					<span className="u-rk">{i + 1}</span>
					<span className="u-pln">{m.name}</span>
					<span className="u-plv" style={{ color }}>{capPct(m.change)}</span>
					<span className="u-plq">{Math.round(m.prev)} → {Math.round(m.cur)} dona</span>
					<div className="u-pltr"><i style={{ width: (Math.abs(m.change) / max * 100) + '%', background: color }} /></div>
				</div>
			))}
		</div>
	)
}

const UNIT_CSS = `
.unit-dash{--st:#4f79e8;--st2:#6f92ee;--mo:#f2996a;--up:#1fae74;--dn:#ec5b72;--ink:#1c2a38;--mut:#6b7c8d;--fnt:#9aa9b8;--line:#e8edf3;--ins:#f2f5fa;--pan:#fff;font-size:14px;color:var(--ink)}
.unit-dash .u-neg{color:var(--dn)!important}
.u-top{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:18px}
.u-brand{display:flex;align-items:center;gap:12px}
.u-mark{width:40px;height:40px;border-radius:10px;background:linear-gradient(150deg,var(--st),var(--st2));display:grid;place-items:center;flex:none}
.u-mark svg{width:22px;height:22px}
.u-h1{margin:0;font-size:19px;font-weight:800;letter-spacing:-.2px}
.u-sub{color:var(--mut);font-size:12.5px;font-weight:500;margin-top:1px}
.u-chips{display:flex;gap:5px;align-items:center;flex-wrap:wrap;background:var(--pan);padding:5px;border-radius:11px;border:1px solid var(--line)}
.u-chip{border:0;background:transparent;color:var(--ink);font:inherit;font-size:12.5px;font-weight:600;padding:7px 12px;border-radius:8px;cursor:pointer}
.u-chip.on{background:var(--st);color:#fff}
.u-dp-input{border:1px solid var(--line);border-radius:8px;padding:7px 10px;font-size:12.5px;width:140px}
.u-empty{padding:40px;text-align:center;color:var(--mut)}
.u-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:13px}
.u-card{background:var(--pan);border:1px solid var(--line);border-radius:13px;padding:15px 16px;box-shadow:0 1px 2px rgba(16,34,46,.04),0 4px 16px rgba(16,34,46,.04)}
.c3{grid-column:span 3}.c4{grid-column:span 4}.c5{grid-column:span 5}.c6{grid-column:span 6}.c7{grid-column:span 7}.c8{grid-column:span 8}.c12{grid-column:1/-1}
@media(max-width:1100px){.c3{grid-column:span 6}.c4,.c5,.c7,.c8{grid-column:span 6}.c6{grid-column:span 6}}
@media(max-width:640px){.u-grid{grid-template-columns:repeat(2,1fr)}.c3,.c4,.c5,.c6,.c7,.c8{grid-column:span 2}}
.u-lbl{font-size:10.5px;letter-spacing:.09em;text-transform:uppercase;color:var(--mut);font-weight:700}
.u-hd{display:flex;align-items:center;gap:8px;margin-bottom:13px;font-size:13px;font-weight:750}
.u-ic{color:var(--st)}
.u-r{margin-left:auto;font-size:11px;color:var(--fnt);font-weight:600;text-transform:none;letter-spacing:0}
.u-kpi .u-big{font-size:25px;font-weight:820;letter-spacing:-.5px;margin:8px 0 4px;line-height:1;font-variant-numeric:tabular-nums}
.u-unit{font-size:14px;color:var(--mut);font-weight:600}
.u-kpi.hero{position:relative;overflow:hidden}
.u-kpi.hero::before{content:"";position:absolute;left:0;top:0;bottom:0;width:4px;background:linear-gradient(var(--mo),var(--st))}
.u-delta{display:inline-flex;align-items:center;gap:3px;font-size:11.5px;font-weight:700;padding:2px 7px;border-radius:20px}
.u-up{color:var(--up);background:rgba(31,174,116,.12)}.u-down{color:var(--dn);background:rgba(236,91,114,.12)}.u-flat{color:var(--fnt);background:var(--ins)}
.u-foot{margin-top:7px;font-size:11.5px;color:var(--fnt);font-weight:500;font-variant-numeric:tabular-nums}
.u-health{display:flex;gap:16px;align-items:center}
.u-ring{width:100px;height:100px;flex:none;border-radius:50%;display:grid;place-items:center}
.u-ring-in{width:75px;height:75px;border-radius:50%;background:var(--pan);display:flex;flex-direction:column;align-items:center;justify-content:center}
.u-ring-in b{font-size:26px;font-weight:820;line-height:1}
.u-ring-in span{font-size:9px;letter-spacing:.06em;text-transform:uppercase;color:var(--mut);font-weight:700}
.u-hrows{flex:1}
.u-hrow{display:flex;align-items:center;gap:9px;padding:7px 0;border-bottom:1px solid var(--ins)}
.u-hrow:last-child{border-bottom:0}
.u-hn{font-size:12px;font-weight:600;color:#33475b;width:110px;flex:none}
.u-bar{flex:1;height:7px;border-radius:6px;background:var(--ins);overflow:hidden}
.u-bar>i{display:block;height:100%;border-radius:6px;background:var(--st)}
.u-hsc{font-size:11.5px;font-weight:700;width:42px;text-align:right;flex:none;font-variant-numeric:tabular-nums}
.u-hrow.warn .u-bar>i{background:var(--mo)}.u-hrow.warn .u-hsc{color:var(--mo)}
.u-hrow.bad .u-bar>i{background:var(--dn)}.u-hrow.bad .u-hsc{color:var(--dn)}
.u-hrow.good .u-hsc{color:var(--up)}
.u-funnel{display:flex;flex-direction:column;gap:8px}
.u-fn{display:flex;align-items:center;gap:10px}
.u-fl{font-size:12px;font-weight:600;color:#33475b;width:88px;flex:none}
.u-fbar{flex:1;height:32px;border-radius:8px;display:flex;align-items:center;padding:0 12px;color:#fff;font-weight:800;font-size:15px;font-variant-numeric:tabular-nums;min-width:44px}
.u-soon{position:relative}
.u-ghost{filter:grayscale(.5);opacity:.65}
.u-veil{position:absolute;inset:0;border-radius:13px;background:rgba(255,255,255,.6);display:grid;place-items:center}
.u-badge{background:rgba(217,119,47,.12);color:var(--mo);font-weight:800;font-size:11.5px;padding:6px 13px;border-radius:20px;border:1px solid var(--mo)}
.u-ptype{display:flex;flex-direction:column;gap:12px}
.u-pt{display:grid;grid-template-columns:1fr auto;gap:4px 10px;align-items:center}
.u-pn{font-weight:700;font-size:13px}
.u-pv{font-weight:800;font-size:14px;font-variant-numeric:tabular-nums}
.u-sw{display:inline-block;width:9px;height:9px;border-radius:3px;margin-right:6px;vertical-align:-1px}
.u-track{grid-column:1/-1;height:9px;border-radius:6px;background:var(--ins);overflow:hidden}
.u-track>i{display:block;height:100%}
.u-meta{grid-column:1/-1;display:flex;gap:14px;font-size:11.5px;color:var(--mut);font-weight:600;flex-wrap:wrap}
.u-meta b{color:#33475b}
.u-plist{display:flex;flex-direction:column;gap:10px}
.u-pl{display:grid;grid-template-columns:18px 1fr auto;gap:8px;align-items:center}
.u-rk{color:var(--fnt);font-weight:800;font-size:12px;text-align:center}
.u-pln{font-weight:650;font-size:12.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.u-plv{font-weight:800;font-size:12.5px;text-align:right;font-variant-numeric:tabular-nums}
.u-plq{grid-column:2/-1;font-size:11px;color:var(--fnt);font-weight:500;font-variant-numeric:tabular-nums}
.u-pltr{grid-column:1/-1;height:5px;border-radius:5px;background:var(--ins);overflow:hidden}
.u-pltr>i{display:block;height:100%;border-radius:5px}
.u-heat{display:grid;grid-template-columns:repeat(24,1fr);gap:4px}
.u-heat>i{aspect-ratio:1/1.5;border-radius:4px;display:block}
.u-heat-ax{grid-column:1/-1;display:flex;justify-content:space-between;font-size:10px;color:var(--fnt);font-weight:600;margin-top:4px}
.u-note{margin-top:9px;font-size:12px;color:var(--mut);font-weight:600}
.u-note b{color:var(--mo)}
.u-week{display:grid;grid-template-columns:repeat(7,1fr);gap:8px;align-items:end;height:92px}
.u-wd{display:flex;flex-direction:column;align-items:center;gap:6px;height:100%;justify-content:flex-end}
.u-wb{width:58%;border-radius:6px 6px 3px 3px;background:var(--st);min-height:4px}
.u-wl{font-size:11px;color:var(--mut);font-weight:600}
.u-wd.best .u-wb{background:var(--up)}.u-wd.worst .u-wb{background:var(--dn)}
.u-wtrend{display:grid;grid-template-columns:repeat(2,1fr);gap:9px}
.u-wt{background:var(--ins);border:1px solid var(--line);border-radius:10px;padding:10px 11px}
.u-wt.cur{border-color:var(--st);box-shadow:0 0 0 1px rgba(40,90,140,.1)}
.u-wtl{font-size:11px;color:var(--mut);font-weight:600}
.u-wtv{font-size:17px;font-weight:820;margin:4px 0 3px;letter-spacing:-.3px;font-variant-numeric:tabular-nums}
.u-list2{display:flex;flex-direction:column}
.u-l2{display:flex;align-items:center;gap:9px;padding:8px 0;border-bottom:1px solid var(--ins)}
.u-l2:last-child{border-bottom:0}
.u-dot{width:8px;height:8px;border-radius:50%;flex:none;background:var(--mo)}
.u-dot.hi{background:var(--dn)}
.u-l2n{font-weight:650;font-size:12.5px;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.u-l2v{font-weight:800;font-size:12.5px;font-variant-numeric:tabular-nums}
.u-sumline{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:10px}
.u-sbig{font-size:21px;font-weight:820;letter-spacing:-.4px;font-variant-numeric:tabular-nums}
.u-sl{font-size:10.5px;color:var(--mut);font-weight:700;text-transform:uppercase;letter-spacing:.06em}
.u-deliv{display:flex;gap:12px}
.u-dv{flex:1;text-align:center;background:var(--ins);border:1px solid var(--line);border-radius:10px;padding:13px 6px}
.u-dn{font-size:20px;font-weight:820;letter-spacing:-.4px;font-variant-numeric:tabular-nums}
.u-dl{font-size:11px;color:var(--mut);font-weight:600;margin-top:2px}
.u-tabs{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:15px}
.u-tab{border:1px solid var(--line);background:var(--pan);color:var(--mut);font:inherit;font-size:12px;font-weight:650;padding:6px 13px;border-radius:20px;cursor:pointer;transition:all .15s}
.u-tab:hover{border-color:var(--st);color:var(--st)}
.u-tab.on{background:var(--st);color:#fff;border-color:var(--st)}
.u-move2{display:grid;grid-template-columns:1fr 1fr;gap:26px}
@media(max-width:640px){.u-move2{grid-template-columns:1fr;gap:18px}}
.u-move-h{display:flex;align-items:center;gap:7px;font-size:12px;font-weight:750;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid var(--ins)}
.u-move-h.up{color:var(--up)}.u-move-h.dn{color:var(--dn)}
`

export default Unit
